import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { CheckCheck, FlaskConical, Hand, Headset, Plus, Send } from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, PriorityBadge } from "@/components/ui/Badge";
import { Input, Select } from "@/components/ui/Field";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";
import { DataTable, formatDate, type Column } from "@/components/ui/DataTable";
import { RichText } from "@/components/RichText";
import { useToast } from "@/components/ui/toast";
import { useSession, isAdmin } from "@/lib/session";
import { cn } from "@/lib/cn";

// --------------------------------------------------------------------------
// Live chat queue
// --------------------------------------------------------------------------

const queueSchema = z.object({
  items: z
    .array(
      z.looseObject({
        id: z.number(),
        ticket: z.string(),
        patient: z.string().nullable().default(null),
        topic: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        priority: z.string().nullable().default(null),
        channel: z.string().nullable().default(null),
        message: z.string().nullable().default(null),
        assigned_to: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

const messagesSchema = z.looseObject({
  messages: z
    .array(
      z.looseObject({
        sender: z.string(),
        content: z.string(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

type Conversation = z.infer<typeof queueSchema>["items"][number];

export function AdminLiveChat() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: session } = useSession();
  const operator = isAdmin(session) ? session.name : null;

  const [selected, setSelected] = useState<string | null>(null);
  const [reply, setReply] = useState("");

  const queue = useQuery({
    queryKey: ["admin", "livechat"],
    queryFn: ({ signal }) => api.get("/api/admin/livechat", queueSchema, signal),
    // A waiting patient is time-critical; the queue must not go stale.
    refetchInterval: 15_000,
  });

  const thread = useQuery({
    queryKey: ["admin", "livechat", selected],
    queryFn: ({ signal }) =>
      api.get(`/api/chat/messages/${selected}`, messagesSchema, signal),
    enabled: Boolean(selected),
    refetchInterval: selected ? 10_000 : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "livechat"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
  };

  const claim = useMutation({
    mutationFn: (ticket: string) =>
      api.post(`/api/conversations/${ticket}/claim`, okSchema, {}),
    onSuccess: (_result, ticket) => {
      invalidate();
      setSelected(ticket);
      toast.success({ title: "Percakapan diklaim" });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal mengklaim",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const close = useMutation({
    mutationFn: (ticket: string) =>
      api.post(`/api/conversations/${ticket}/close`, okSchema, {}),
    onSuccess: () => {
      invalidate();
      setSelected(null);
      toast.success({ title: "Percakapan ditutup" });
    },
  });

  const send = useMutation({
    mutationFn: () =>
      api.post("/api/chat/send", okSchema, { ticket: selected, message: reply.trim() }),
    onSuccess: () => {
      setReply("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "livechat", selected] });
    },
    onError: (cause) =>
      toast.danger({
        title: "Pesan gagal terkirim",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  const columns: Column<Conversation>[] = [
    {
      key: "priority",
      header: "Prioritas",
      width: "120px",
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: "patient",
      header: "Pasien",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.patient ?? "Tamu"}</p>
          <p className="text-content-subtle tabular truncate text-xs">{row.ticket}</p>
        </div>
      ),
    },
    {
      key: "topic",
      header: "Topik",
      render: (row) => <span className="text-content-muted text-xs">{row.topic ?? "—"}</span>,
    },
    {
      key: "waiting",
      header: "Masuk",
      width: "150px",
      render: (row) => (
        <span className="text-content-muted text-xs">{formatDate(row.created_at, true)}</span>
      ),
    },
    {
      key: "assigned",
      header: "Operator",
      width: "140px",
      render: (row) =>
        row.assigned_to ? (
          <Badge tone={row.assigned_to === operator ? "accent" : "neutral"}>
            {row.assigned_to}
          </Badge>
        ) : (
          <span className="text-content-subtle text-xs">Belum diklaim</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      width: "190px",
      render: (row) => (
        <div className="flex justify-end gap-1.5">
          {!row.assigned_to && (
            <Button
              size="sm"
              icon={<Hand className="size-3.5" />}
              loading={claim.isPending && claim.variables === row.ticket}
              onClick={() => claim.mutate(row.ticket)}
            >
              Klaim
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(row.ticket)}>
            Buka
          </Button>
        </div>
      ),
    },
  ];

  const waiting = (queue.data?.items ?? []).filter((item) => !item.assigned_to).length;

  return (
    <>
      <PageHeader
        eyebrow="Operasional"
        title="Live Chat"
        description="Percakapan yang membutuhkan operator manusia."
        actions={
          waiting > 0 ? <Badge tone="urgent">{waiting} menunggu</Badge> : undefined
        }
      />

      {queue.error && (
        <Alert tone="danger" title="Gagal memuat antrean" className="mb-4">
          {queue.error instanceof ApiError ? queue.error.message : "Terjadi kesalahan."}
        </Alert>
      )}

      {/* items-start: with nothing selected the detail panel is a one-line
          empty state, and stretching it to the queue's height left ~600px of
          blank card. */}
      <div className="grid gap-4 lg:grid-cols-5 lg:items-start">
        <Card className="lg:col-span-3">
          <CardHeader eyebrow="Antrean" title="Menunggu penanganan" />
          <DataTable
            columns={columns}
            rows={queue.data?.items ?? []}
            rowKey={(row) => row.id}
            isPending={queue.isPending}
            onRowClick={(row) => setSelected(row.ticket)}
            empty={{
              icon: <Headset className="size-5" />,
              title: "Tidak ada antrean",
              description: "Semua percakapan sedang ditangani bot.",
            }}
          />
        </Card>

        <Card className="flex max-h-[70vh] flex-col lg:col-span-2">
          <CardHeader
            eyebrow={selected ? `Tiket ${selected}` : "Percakapan"}
            title={selected ? "Riwayat pesan" : "Pilih percakapan"}
            action={
              selected ? (
                <Button
                  size="sm"
                  icon={<CheckCheck className="size-3.5" />}
                  loading={close.isPending}
                  onClick={() => close.mutate(selected)}
                >
                  Tutup
                </Button>
              ) : undefined
            }
          />

          {!selected ? (
            <EmptyState
              icon={<Headset className="size-5" />}
              title="Belum ada yang dipilih"
              description="Klik salah satu baris antrean untuk membuka percakapannya."
            />
          ) : thread.isPending ? (
            <LoadingPanel />
          ) : (
            <>
              <ul className="scrollbar-slim min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
                {thread.data?.messages.length === 0 && (
                  <li className="text-content-subtle text-center text-sm">Belum ada pesan.</li>
                )}
                {thread.data?.messages.map((message, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex",
                      message.sender === "user" ? "justify-start" : "justify-end",
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm",
                        message.sender === "user"
                          ? "bg-surface-sunken"
                          : "bg-accent text-accent-content",
                      )}
                    >
                      <RichText text={message.content} />
                      <p className="mt-1 text-2xs opacity-60">
                        {formatDate(message.created_at, true)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (reply.trim()) send.mutate();
                }}
                className="border-hairline flex gap-2 border-t p-3"
              >
                <Input
                  placeholder="Tulis balasan…"
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  className="flex-1"
                  aria-label="Balasan operator"
                />
                <Button
                  type="submit"
                  variant="primary"
                  loading={send.isPending}
                  disabled={!reply.trim()}
                  aria-label="Kirim balasan"
                  icon={<Send className="size-4" />}
                />
              </form>
            </>
          )}
        </Card>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Patient lab results (admin)
// --------------------------------------------------------------------------

const labsSchema = z.object({
  patient: z.looseObject({
    rm_number: z.string(),
    name: z.string(),
    dob: z.string().nullable().default(null),
    gender: z.string().nullable().default(null),
  }),
  items: z
    .array(
      z.looseObject({
        id: z.number(),
        test_name: z.string(),
        result_value: z.string().nullable().default(null),
        unit: z.string().nullable().default(null),
        reference_range: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        result_date: z.string().nullable().default(null),
        notes: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

const LAB_STATUSES = ["Normal", "Rendah", "Tinggi", "Kritis"];

export function AdminPatientLabs() {
  const { rm } = useParams<{ rm: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({
    test_name: "",
    result_value: "",
    unit: "",
    reference_range: "",
    status: LAB_STATUSES[0],
    result_date: new Date().toISOString().slice(0, 10),
  });

  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "labs", rm],
    queryFn: ({ signal }) => api.get(`/api/admin/patients/${rm}/labs`, labsSchema, signal),
    enabled: Boolean(rm),
  });

  const add = useMutation({
    mutationFn: () => api.post(`/api/admin/patients/${rm}/labs`, okSchema, form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "labs", rm] });
      setForm({ ...form, test_name: "", result_value: "", unit: "", reference_range: "" });
      toast.success({
        title: "Hasil lab ditambahkan",
        description: "Pasien menerima notifikasi di portal.",
      });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal menambahkan",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  if (error) {
    return (
      <>
        <PageHeader title="Hasil Laboratorium" />
        <Alert tone="danger" title="Gagal memuat">
          {error instanceof ApiError ? error.message : "Terjadi kesalahan."}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={data ? `${data.patient.rm_number} · ${data.patient.name}` : "Memuat…"}
        title="Hasil Laboratorium"
        description="Hasil yang ditambahkan langsung tampil di Health Hub pasien."
      />

      <Card className="mb-4">
        <CardHeader eyebrow="Input" title="Tambah hasil" />
        <CardBody>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate();
            }}
            className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:items-end"
          >
            <Input
              className="lg:col-span-2"
              label="Nama pemeriksaan"
              value={form.test_name}
              onChange={(e) => setForm({ ...form, test_name: e.target.value })}
              required
            />
            <Input
              label="Hasil"
              value={form.result_value}
              onChange={(e) => setForm({ ...form, result_value: e.target.value })}
            />
            <Input
              label="Satuan"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            />
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {LAB_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
            <Button type="submit" variant="primary" loading={add.isPending} icon={<Plus className="size-4" />}>
              Tambah
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        {isPending ? (
          <LoadingPanel />
        ) : data && data.items.length > 0 ? (
          <ul className="divide-hairline divide-y">
            {data.items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{item.test_name}</p>
                  <p className="text-content-muted tabular text-xs">
                    {item.result_value} {item.unit}
                    {item.reference_range && ` (acuan ${item.reference_range})`}
                  </p>
                </div>
                <span className="text-content-subtle shrink-0 text-xs">
                  {formatDate(item.result_date)}
                </span>
                <Badge tone={item.status === "Normal" ? "routine" : "monitor"}>
                  {item.status ?? "—"}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<FlaskConical className="size-5" />}
            title="Belum ada hasil lab"
            description="Tambahkan hasil menggunakan formulir di atas."
          />
        )}
      </Card>
    </>
  );
}
